import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Alert
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

// Services
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useStore } from '../store/useStore';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    const auth = getAuth();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      
      // Obtener información adicional del usuario desde Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        let userRole = 'user'; // Rol por defecto
        let userName = userCredential.user.displayName;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userRole = userData.role || 'user';
          userName = userData.name || userCredential.user.displayName;
        }
        
        const userWithRole = {
          ...userCredential.user,
          name: userName,
          email: userCredential.user.email,
          role: userRole
        };
        
        login(userWithRole, null);
        toast.success('¡Bienvenido de nuevo!');
        
        // Redirigir según el rol del usuario
        if (userRole === 'admin') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      } catch (firestoreError) {
        console.error('Error obteniendo datos de usuario:', firestoreError);
        // Si falla Firestore, usar datos básicos
        login({
          ...userCredential.user,
          name: userCredential.user.displayName,
          email: userCredential.user.email,
          role: 'user'
        }, null);
        toast.success('¡Bienvenido de nuevo!');
        navigate('/');
      }
    } catch (error) {
      const message = error.message || 'Error al iniciar sesión';
      // Manejar error específico de configuración de Firebase
      if (error.code === 'auth/configuration-not-found') {
        setError('Error de configuración. Por favor, contacta al administrador.');
        toast.error('Error de configuración del sistema');
      } else {
        setError(message);
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              mb: 1
            }}
          >
            Iniciar Sesión
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Accede a tu cuenta de Vinogenes
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            margin="normal"
            {...register('email', {
              required: 'El email es obligatorio',
              pattern: {
                value: /^\S+@\S+$/i,
                message: 'Email no válido'
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <TextField
            fullWidth
            label="Contraseña"
            type="password"
            margin="normal"
            {...register('password', {
              required: 'La contraseña es obligatoria',
              minLength: {
                value: 6,
                message: 'La contraseña debe tener al menos 6 caracteres'
              }
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem'
            }}
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>

        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 